/**
 * Allergen keyword classifier (D31). Turns an ingredient's canonical name into
 * the `ALLERGENS` keys it (probably) contains: "peanut butter" → peanut,
 * "soy sauce" → soy + gluten, "almond flour" → tree_nut (not gluten).
 *
 * Deliberately conservative toward *detecting*: a false "contains" only costs
 * a recipe one planner slot, a missed allergen can hurt someone. Still, an
 * empty result means "nothing detected", never "safe" — the planner keeps
 * showing "contains" chips so people can check.
 *
 * Each rule removes its `except` phrases from the name before testing
 * `match`, so "coconut milk and butter" still finds the butter.
 */
/** Keep in sync with `ALLERGENS` in packages/schema/src/profiles.ts (sync test). */
export const ALLERGENS = [
    'peanut',
    'tree_nut',
    'dairy',
    'egg',
    'gluten',
    'soy',
    'shellfish',
    'fish',
    'sesame',
];
const PLANT_MILKS = /\b(coconut|almond|oat|soy|soya|rice|cashew|hemp|pea|flax|macadamia|plant|vegan|non[- ]?dairy|dairy[- ]free)[\s-]+(milk|cream|creamer|butter|cheese|yogh?urt|ice cream)s?\b/g;
const NOT_DAIRY = new RegExp([
    PLANT_MILKS.source,
    String.raw `\b(peanut|nut|apple|cocoa|cacao|seed|sunflower[\s-]+seed|shea|pumpkin|pecan|walnut|hazelnut|pistachio)[\s-]+butters?\b`,
    String.raw `\bbutter(nut|[\s-]+beans?|[\s-]+lettuce|scotch)\b`,
    String.raw `\bcream of tartar\b`,
    String.raw `\bcream[- ]style corn\b`,
    String.raw `\bcoconut cream\b`,
    String.raw `\bmilk thistle\b`,
].join('|'), 'g');
const GLUTEN_FREE = /\bgluten[- ]free\b[\w\s-]*/g;
const NOT_GLUTEN_FLOUR = /\b(almond|coconut|rice|corn|chickpea|garbanzo|tapioca|cassava|potato|oat|buckwheat|sorghum|millet|quinoa|teff|arrowroot|banana|cricket|hazelnut|lupin|gluten[- ]free)[\s-]+(flour|meal|starch)s?\b/g;
const NOT_GLUTEN_NOODLE = /\b(rice|glass|cellophane|bean thread|shirataki|zucchini|kelp|sweet potato|konjac|mung bean)[\s-]+(noodles?|vermicelli|pasta|spaghetti)\b/g;
const NOT_TREE_NUT = /\b(nutmeg|butternut|doughnut|donut|coconut|water chestnut|chestnut mushroom|peanut)s?\b/g;
/** Table-driven: ≥ 40 rules. Order does not matter (results are sorted by `ALLERGENS`). */
export const ALLERGEN_RULES = [
    // ---- peanut
    { allergen: 'peanut', match: /\bpeanuts?\b/ },
    { allergen: 'peanut', match: /\bgroundnuts?\b/ },
    { allergen: 'peanut', match: /\bsatay\b/ },
    // ---- tree nuts
    { allergen: 'tree_nut', match: /\balmonds?\b/ },
    { allergen: 'tree_nut', match: /\bcashews?\b/ },
    { allergen: 'tree_nut', match: /\bwalnuts?\b/ },
    { allergen: 'tree_nut', match: /\bpecans?\b/ },
    { allergen: 'tree_nut', match: /\bpistachios?\b/ },
    { allergen: 'tree_nut', match: /\bhazelnuts?\b|\bfilberts?\b|\bnutella\b/ },
    { allergen: 'tree_nut', match: /\bmacadamias?\b/ },
    { allergen: 'tree_nut', match: /\bbrazil nuts?\b|\bpine nuts?\b|\bpignoli\b/ },
    { allergen: 'tree_nut', match: /\bpraline\b|\bmarzipan\b|\bamaretto\b|\bfrangipane\b/ },
    { allergen: 'tree_nut', match: /\bpesto\b/ },
    { allergen: 'tree_nut', match: /\b(mixed )?nuts\b/, except: NOT_TREE_NUT },
    // ---- dairy
    { allergen: 'dairy', match: /\bmilks?\b/, except: NOT_DAIRY },
    { allergen: 'dairy', match: /\bbuttermilk\b|\bhalf[- ]and[- ]half\b/ },
    { allergen: 'dairy', match: /\bbutter\b/, except: NOT_DAIRY },
    { allergen: 'dairy', match: /\bghee\b/ },
    { allergen: 'dairy', match: /\bcreams?\b|\bcreamer\b/, except: NOT_DAIRY },
    { allergen: 'dairy', match: /\bcr[eè]me fra[iî]che\b/ },
    { allergen: 'dairy', match: /\bcheeses?\b/, except: NOT_DAIRY },
    { allergen: 'dairy', match: /\byogh?urts?\b|\bkefir\b|\bskyr\b/, except: NOT_DAIRY },
    {
        allergen: 'dairy',
        match: /\b(parmesan|parmigiano|pecorino|mozzarella|cheddar|ricotta|feta|gouda|brie|camembert|mascarpone|gruy[eè]re|provolone|asiago|burrata|halloumi|paneer|cotija|queso|monterey jack|colby|emmental|havarti|manchego|gorgonzola|roquefort|stilton|fontina|neufch[aâ]tel|quark|labneh)\b/,
    },
    { allergen: 'dairy', match: /\bwhey\b|\bcasein(ate)?\b|\blactose\b/ },
    { allergen: 'dairy', match: /\bcustard\b|\bgelato\b|\bdulce de leche\b|\btzatziki\b/ },
    { allergen: 'dairy', match: /\bpesto\b/ },
    // ---- egg
    { allergen: 'egg', match: /\beggs?\b|\begg whites?\b|\byolks?\b/ },
    { allergen: 'egg', match: /\bmayo(nnaise)?\b|\baioli\b|\bmeringue\b/ },
    { allergen: 'egg', match: /\bcustard\b|\bhollandaise\b|\bb[eé]arnaise\b/ },
    // ---- gluten
    {
        allergen: 'gluten',
        match: /\bflours?\b/,
        except: new RegExp(`${NOT_GLUTEN_FLOUR.source}|${GLUTEN_FREE.source}`, 'g'),
    },
    {
        allergen: 'gluten',
        match: /\bbreads?\b|\bbread ?crumbs?\b|\bpanko\b|\bcroutons?\b|\bstuffing\b/,
        except: GLUTEN_FREE,
    },
    {
        allergen: 'gluten',
        match: /\bpasta\b|\bspaghetti\b|\bmacaroni\b|\bpenne\b|\bfettuccine\b|\blinguine\b|\blasagn[ae]\b|\borzo\b|\bravioli\b|\btortellini\b|\brigatoni\b|\bgnocchi\b/,
        except: new RegExp(`${NOT_GLUTEN_NOODLE.source}|${GLUTEN_FREE.source}`, 'g'),
    },
    {
        allergen: 'gluten',
        match: /\bnoodles?\b|\budon\b|\bramen\b|\bsoba\b|\begg noodles?\b/,
        except: new RegExp(`${NOT_GLUTEN_NOODLE.source}|${GLUTEN_FREE.source}`, 'g'),
    },
    {
        allergen: 'gluten',
        match: /\bwheat\b|\bsemolina\b|\bdurum\b|\bspelt\b|\bfarro\b|\bbulgu?r\b/,
        except: GLUTEN_FREE,
    },
    {
        allergen: 'gluten',
        match: /\bbarley\b|\brye\b|\bmalt\b|\bseitan\b|\bcouscous\b/,
        except: GLUTEN_FREE,
    },
    { allergen: 'gluten', match: /\bsoy sauce\b|\bteriyaki\b|\bhoisin\b/, except: GLUTEN_FREE },
    {
        allergen: 'gluten',
        match: /\bcrackers?\b|\bpita\b|\bbagels?\b|\bbuns?\b|\brolls?\b|\bbaguette\b|\bbrioche\b|\bcroissants?\b|\bnaan\b|\bpuff pastry\b|\bpie crust\b|\bphyllo\b|\bfilo\b|\bwonton\b|\bdumpling wrappers?\b|\btortillas?\b/,
        except: new RegExp(`\\b(corn|rice paper) (tortillas?|wrappers?)\\b|${GLUTEN_FREE.source}`, 'g'),
    },
    { allergen: 'gluten', match: /\bbeers?\b|\bales?\b|\blager\b|\bstout\b/ },
    // ---- soy
    {
        allergen: 'soy',
        match: /\bsoy(a|beans?)?\b|\bsoy sauce\b|\bshoyu\b|\btamari\b|\bteriyaki\b|\bhoisin\b/,
    },
    {
        allergen: 'soy',
        match: /\btofu\b|\bedamame\b|\bmiso\b|\btempeh\b|\bnatto\b|\bdoenjang\b|\bgochujang\b/,
    },
    // ---- shellfish (crustaceans and molluscs)
    { allergen: 'shellfish', match: /\bshrimps?\b|\bprawns?\b|\bscampi\b/ },
    {
        allergen: 'shellfish',
        match: /\bcrabs?\b|\blobsters?\b|\bcraw(fish|dad)\b|\bcrayfish\b|\blangoustines?\b/,
    },
    { allergen: 'shellfish', match: /\bclams?\b|\bmussels?\b|\bcockles?\b|\bwhelks?\b|\babalone\b/ },
    {
        allergen: 'shellfish',
        match: /\boysters?\b/,
        except: /\boyster mushrooms?\b/g,
    },
    {
        allergen: 'shellfish',
        match: /\bscallops?\b|\bsquid\b|\bcalamari\b|\boctopus\b|\bshellfish\b/,
    },
    // ---- fish
    {
        allergen: 'fish',
        match: /\bsalmon\b|\btuna\b|\bcod\b|\btilapia\b|\bhalibut\b|\btrout\b|\bsardines?\b|\bmackerel\b|\bhaddock\b|\bpollock\b|\bmahi[- ]?mahi\b|\bsnapper\b|\bcatfish\b|\bsea bass\b|\bbass\b|\bswordfish\b|\bflounder\b|\bsole\b|\bherring\b|\bbranzino\b|\bgrouper\b/,
    },
    {
        allergen: 'fish',
        match: /\banchov(y|ies)\b|\bfish sauce\b|\bworcestershire\b|\bbonito\b|\bdashi\b/,
    },
    { allergen: 'fish', match: /\bfish\b|\bcaviar\b|\broe\b/, except: /\bshellfish\b/g },
    // ---- sesame
    {
        allergen: 'sesame',
        match: /\bsesame\b|\btahini\b|\bbenne\b|\bhalva\b|\bza'?atar\b|\bgomashio\b|\bhummus\b/,
    },
];
function normalise(name) {
    return ` ${name
        .normalize('NFKC')
        .toLowerCase()
        .replace(/[‘’]/g, "'")
        .replace(/[,;()]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()} `;
}
/** The `ALLERGENS` keys an ingredient name contains, in vocabulary order. */
export function inferAllergens(canonicalName, rules = ALLERGEN_RULES) {
    const name = normalise(canonicalName);
    const found = new Set();
    for (const rule of rules) {
        if (found.has(rule.allergen))
            continue;
        const text = rule.except ? name.replace(new RegExp(rule.except.source, 'g'), ' ') : name;
        if (rule.match.test(text))
            found.add(rule.allergen);
    }
    return ALLERGENS.filter((a) => found.has(a));
}
/** Union of the allergens of several ingredients, in vocabulary order. */
export function unionAllergens(lists) {
    const all = new Set();
    for (const list of lists)
        for (const a of list)
            all.add(a);
    return ALLERGENS.filter((a) => all.has(a));
}
//# sourceMappingURL=allergens.js.map